/**
 * VendorStaffView
 *
 * The vendor's My Team page, built on /api/vendor-team (docs/commerce/
 * vendor-team-roles.md). Who works for this vendor, which role each person
 * holds, the per-person exceptions on top, the vendor's own custom roles, and
 * the POS terminal PINs.
 *
 * Who sees what:
 *   - the owner, and any member holding team:manage, gets the full console:
 *     add, edit, role select, exceptions, resend invite, suspend / reactivate,
 *     remove, custom roles, PINs;
 *   - a member without team:manage sees names and roles only, read through
 *     the legacy helper list because the members route refuses them;
 *   - a platform admin picks a vendor first and then gets the owner's console
 *     for that vendor (the backend admits them via platform permissions).
 *
 * Lusuku Support (a super admin signed in as this vendor) also gets "Set
 * password" on each member row: set their sign-in email and password and send
 * it to them by hand, for when the invite email cannot get through. The calls
 * go out as the admin, never as the vendor, and the owner never sees the
 * button: a shop owner gives their team access through the emailed invite.
 *
 * Branch access (4 Oct 2026): when the vendor has branches, each member has a
 * "Branches" cell (All branches, the branch names, or "{n} branches") and a
 * Branches row action, and the add dialog asks for it. A member limited to
 * some branches sees and runs only those. The page itself stays head office
 * only: team:manage is never held by a branch member.
 *
 * A helper who predates roles (source LEGACY_MEMBER) is shown as
 * "Manager (default)" until the owner picks a role for them; nothing about
 * their access changes until then.
 *
 * Every dialog, dropdown and button is the dashboard kit (components/ui) and
 * every team concept (role chip, role dropdown, role editor, exceptions) is
 * shared with the organizer team page through components/team, so the two
 * pages explain roles the same way and look the same.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { getUserFromLocalStorage } from "../../utilities/user";
import { apiEntitySearch } from "../../api";
import {
  apiVendorTeamCatalogue,
  apiVendorTeamMembers,
  apiVendorTeamAddMember,
  apiVendorTeamChangeRole,
  apiVendorTeamSetOverrides,
  apiVendorTeamSetStatus,
  apiVendorTeamRemoveMember,
  apiVendorTeamUpdateMemberDetails,
  apiVendorTeamLegacyHelpers,
  apiVendorTeamResendInvite,
  apiVendorTeamCreateRole,
  apiVendorTeamUpdateRole,
  apiVendorTeamDeleteRole,
  apiVendorTeamLocations,
  apiVendorTeamSetBranches,
  unwrapVendorTeam,
} from "../../api/vendorTeam";
import BranchAccessField from "../../components/team/BranchAccessField";
import {
  branchAwareMessage,
  branchScopeError,
  branchScopeNames,
  joinBranchNames,
  normalizeBranchScope,
} from "../../utilities/vendorScope";
import { useVendorAccess } from "../../hooks/useVendorAccess";
// How roles and exceptions work: views/Settings/help/vendorSettingsHelp.js.
import SettingHelp from "../Settings/settingsHelp";
import PosPinManager from "../../components/Pos/PosPinManager";
import EmailDeliveryBadge from "../../components/EmailDeliveryBadge";
import SetVendorLoginDialog from "../../components/VendorCredentials/SetVendorLoginDialog";
import * as impersonation from "../../utilities/impersonation.js";
import { AppButton, AppDialog, AppConfirmDialog } from "../../components/ui";
import {
  RoleChip,
  StatusPill,
  OverridesModal,
  RoleEditorDialog,
  TeamRoleSelect,
  LoadingCenter,
  Alert,
} from "../../components/team";

const DEFAULT_ROLE = "MANAGER";
const LEGACY_ROLE_LABEL = "Manager (default)";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const WHOLE_BUSINESS = normalizeBranchScope(null);
const EMPTY_FORM = { firstName: "", lastName: "", email: "", roleKey: DEFAULT_ROLE, branchScope: WHOLE_BUSINESS };
// More names than this and the cell says "{n} branches", with the names on hover.
const MAX_BRANCH_CHIPS = 3;
/** Did a member's branch access change? Compared in the server's own shape. */
const sameBranchScope = (a, b) => {
  const x = normalizeBranchScope(a);
  const y = normalizeBranchScope(b);
  return x.mode === y.mode && x.includeMain === y.includeMain &&
    [...x.outletIds].sort().join(",") === [...y.outletIds].sort().join(",");
};
const EMPTY_ERRORS = { firstName: "", lastName: "", email: "" };

const apiErr = (err, fallback) => err?.response?.data?.message || err?.message || fallback;

/** The Branches cell: "All branches", up to three names, or "{n} branches". */
function BranchCell({ branchScope, locations }) {
  const { all, names } = branchScopeNames(branchScope, locations);
  if (all) return <span className="lk-pill lk-branch-chip lk-branch-chip--muted">All branches</span>;
  if (!names.length) {
    return (
      <span className="lk-pill lk-branch-chip lk-branch-chip--warn" title="They see nothing until you give them a branch.">
        No branch
      </span>
    );
  }
  if (names.length > MAX_BRANCH_CHIPS) {
    return (
      <span className="lk-pill lk-branch-chip" title={names.join(", ")}>
        {names.length} branches
      </span>
    );
  }
  return (
    <span className="lk-branch-chips">
      {names.map((name, i) => (
        <span
          key={`${name}-${i}`}
          className={`lk-pill lk-branch-chip${name === "Deleted branch" ? " lk-branch-chip--muted" : ""}`}
          title={name === "Deleted branch" ? "This branch was deleted. Use Branches to choose another one for them." : undefined}
        >
          {name}
        </span>
      ))}
    </span>
  );
}
const fullName = (m) => [m?.firstName, m?.lastName].filter(Boolean).join(" ") || m?.name || m?.email || "Unnamed";
const initials = (m) => `${(m?.firstName?.[0] || m?.name?.[0] || "?").toUpperCase()}${(m?.lastName?.[0] || "").toUpperCase()}`;

/** "just now", "4 min ago", "3 h ago", "2 Oct" */
const ago = (when) => {
  if (!when) return "";
  const t = new Date(when).getTime();
  if (!Number.isFinite(t)) return "";
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 45) return "just now";
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return new Date(t).toLocaleDateString("en-UG", { day: "numeric", month: "short" });
};

/* The legacy helper list is the only roster a member without team:manage may
   read. Shape it like a members row so the table renders one way. */
const fromLegacyHelper = (h) => ({
  userId: String(h._id),
  firstName: h.firstName,
  lastName: h.lastName,
  name: fullName(h),
  email: h.email,
  phoneNumber: h.phoneNumber,
  isActive: h.isActive !== false,
  roleKey: h.roleKey || DEFAULT_ROLE,
  source: h.source || "LEGACY_MEMBER",
  status: h.status || (h.isActive === false ? "SUSPENDED" : "ACTIVE"),
  customPermissions: h.customPermissions || [],
});

// ─── Page layout tokens (dialogs, dropdowns and buttons come from the kit) ───
const S = {
  page: { padding: "0 0 40px", background: "#f8fafc", minHeight: "100vh" },
  card: {
    margin: "24px 28px",
    background: "#fff",
    borderRadius: 14,
    boxShadow: "0 1px 3px rgba(15,23,42,0.06), 0 6px 20px rgba(15,23,42,0.05)",
    overflow: "hidden",
  },
  cardHeader: {
    padding: "18px 24px",
    borderBottom: "1px solid #eef2f6",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    flexWrap: "wrap",
  },
  cardTitle: { fontSize: 15, fontWeight: 700, color: "#0f172a", margin: 0, display: "flex", alignItems: "center" },
  cardSubtitle: { fontSize: 12, color: "#64748b", margin: "3px 0 0" },
  cardBody: { padding: "18px 24px 22px" },
  count: { fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 10px" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: {
    padding: "10px 12px",
    textAlign: "left",
    fontSize: 11,
    fontWeight: 700,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    borderBottom: "1px solid #eef2f6",
    whiteSpace: "nowrap",
  },
  td: { padding: "12px", borderBottom: "1px solid #f1f5f9", color: "#1e293b", verticalAlign: "middle", fontSize: 13 },
  muted: { color: "#64748b", fontSize: 12 },
  avatar: {
    width: 34,
    height: 34,
    borderRadius: "50%",
    background: "#083860",
    color: "#fff",
    fontWeight: 700,
    fontSize: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  emptyState: { textAlign: "center", padding: "48px 24px", color: "#64748b" },
};

/** The invite line under a member's email: has the email gone, did it fail. */
function InviteStatus({ member, deliveryRef }) {
  const invite = member.lastInvite;
  if (invite?.mode === "set_by_admin") {
    if (member.hasSignedIn) return null;
    return (
      <div style={{ ...S.muted, marginTop: 3 }}>
        <i className="fa fa-key" aria-hidden="true" /> Password set by Lusuku Support {ago(invite.at)}, not signed in yet
      </div>
    );
  }
  if (invite && !invite.sent) {
    return (
      <div style={{ ...S.muted, color: "#b91c1c", marginTop: 3 }}>
        <i className="fa fa-exclamation-circle" aria-hidden="true" /> Invite email failed {ago(invite.at)}
      </div>
    );
  }
  if (member.hasSignedIn) return null;
  return (
    <div style={{ ...S.muted, marginTop: 3 }}>
      <i className="far fa-clock" aria-hidden="true" />{" "}
      {invite?.sent ? `Invited ${ago(invite.at)}, not signed in yet` : "Not signed in yet"}
      {deliveryRef ? <div style={{ marginTop: 4 }}><EmailDeliveryBadge refId={deliveryRef} /></div> : null}
    </div>
  );
}

/** First name, last name, email: the same fields for add and edit. */
function MemberFields({ values, errors, onChange, onEnter }) {
  const field = (key, label, props = {}) => (
    <div className="lk-field">
      <label className="lk-field__label" htmlFor={`member-${key}`}>{label}</label>
      <input
        id={`member-${key}`}
        className={`lk-input${errors[key] ? " is-invalid" : ""}`}
        value={values[key]}
        onChange={(e) => onChange({ ...values, [key]: e.target.value })}
        onKeyDown={(e) => e.key === "Enter" && onEnter()}
        aria-invalid={Boolean(errors[key]) || undefined}
        {...props}
      />
      {errors[key] ? <span className="lk-field__error">{errors[key]}</span> : null}
    </div>
  );
  return (
    <>
      <div className="lk-field-grid">
        {field("firstName", "First name", { placeholder: "Jane", "data-autofocus": true })}
        {field("lastName", "Last name", { placeholder: "Doe" })}
      </div>
      {field("email", "Email address", { type: "email", placeholder: "jane@example.com" })}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
const VendorStaffView = () => {
  const { agent } = useSelector((s) => s.auth);
  const user = agent || getUserFromLocalStorage();
  const me = user?.result || {};
  const access = useVendorAccess();

  /* PLATFORM ADMINS MANAGE A VENDOR'S TEAM ON ITS BEHALF.
     Without this the view silently resolved to the admin's OWN user id, so an
     ops person onboarding a vendor saw an empty team and had no way to add
     anyone. The backend admits platform admins through their platform
     permissions; this is the UI half. */
  const isPlatformAdmin =
    !me.parentVendorId &&
    (me.isSuperAdmin === true ||
      ["ADMIN", "SUPERUSER", "SUPERADMIN"].includes(String(me.user_role || me.role || "").toUpperCase()));

  const [adminVendor, setAdminVendor] = useState(null);
  const [vendorQuery, setVendorQuery] = useState("");
  const [vendorResults, setVendorResults] = useState([]);
  const [vendorSearching, setVendorSearching] = useState(false);
  const vendorTimer = useRef(null);

  // The vendor scope is always the OWNER's Users id.
  const vendorId = isPlatformAdmin ? adminVendor?.id || "" : access.vendorUserId || me.parentVendorId || me._id || "";

  // An admin acting on a vendor gets the owner's console for that vendor.
  // For a seller this is the team:manage key (owner always holds it).
  const canManage = isPlatformAdmin ? Boolean(adminVendor) : access.can("team:manage");
  const readOnlyMember = !isPlatformAdmin && access.loaded && !canManage;

  const [catalogue, setCatalogue] = useState({ permissions: [], roles: [], baselinePermissions: [], legacyImplicitRole: DEFAULT_ROLE });
  const [owner, setOwner] = useState(null);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [modalError, setModalError] = useState("");

  // Add / edit
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState(EMPTY_ERRORS);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [editErrors, setEditErrors] = useState(EMPTY_ERRORS);
  const [saving, setSaving] = useState(false);

  // Exceptions, roles, confirmations, invites
  const [overridesFor, setOverridesFor] = useState(null);
  const [roleEditor, setRoleEditor] = useState(null); // { role: null | role }
  const [confirmState, setConfirmState] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [deliveryRefs, setDeliveryRefs] = useState({});

  // Branch access: the vendor's locations, and the member whose branches are
  // being changed. Without a branch the column, the action and the add field
  // stay hidden, so a single-site shop sees the page it always has.
  const [locations, setLocations] = useState([]);
  const [branchesFor, setBranchesFor] = useState(null);
  const [branchDraft, setBranchDraft] = useState(WHOLE_BUSINESS);
  const [branchTried, setBranchTried] = useState(false);
  const hasBranches = locations.some((l) => !l.isMain);
  // The column and the Branches action also stay while any member is still
  // limited to branches, so the owner can fix someone whose last branch was
  // deleted (they would otherwise see nothing and nobody could say why).
  const showBranches = hasBranches || staff.some((m) => normalizeBranchScope(m.branchScope).mode === "BRANCHES");

  /* LUSUKU SUPPORT, SIGNED IN AS THIS VENDOR. Only super admins can sign in
     as a vendor (authController.startVendorImpersonation), so a live vendor
     session that names its storefront is the signal to offer "Set password"
     on member rows. Read once: the session cannot change under this page. */
  const supportSession = useMemo(() => {
    const meta = impersonation.active();
    return meta && !meta.expired && meta.vendorId && meta.vertical ? meta : null;
  }, []);
  const [setLoginFor, setSetLoginFor] = useState(null);

  const roleByKey = useMemo(() => {
    const m = new Map();
    for (const r of catalogue.roles || []) m.set(r.key, r);
    return m;
  }, [catalogue.roles]);
  const roleLabel = useCallback((key) => roleByKey.get(key)?.label || key, [roleByKey]);

  // What this login may hand out: everything for the owner and platform
  // admins, otherwise only what they hold themselves.
  const grantableKeys = useMemo(
    () => new Set(isPlatformAdmin || access.isOwner
      ? (catalogue.permissions || []).map((p) => p.key)
      : access.permissions || []),
    [isPlatformAdmin, access.isOwner, access.permissions, catalogue.permissions]
  );

  // ─────────────────────────────────────────────────────────────────────────
  const fetchStaff = useCallback(async ({ silent } = {}) => {
    if (!vendorId) {
      // An admin who has not picked a vendor yet has nothing to load.
      setStaff([]);
      setOwner(null);
      setLoading(false);
      return;
    }
    // Wait for the access set; a failed fetch fails open (loading false,
    // loaded false) and the console loads with the server as the gate.
    if (!isPlatformAdmin && access.loading) return;
    if (!silent) setLoading(true);
    setError(null);
    try {
      const catReq = apiVendorTeamCatalogue(vendorId).then(unwrapVendorTeam).catch(() => null);
      if (canManage) {
        const [cat, list] = await Promise.all([catReq, apiVendorTeamMembers(vendorId).then(unwrapVendorTeam)]);
        if (cat) setCatalogue(cat);
        setOwner(list.owner || null);
        setStaff(list.members || []);
      } else {
        const [cat, legacy] = await Promise.all([catReq, apiVendorTeamLegacyHelpers(vendorId)]);
        if (cat) setCatalogue(cat);
        setOwner(null);
        setStaff((legacy.data?.sellerHelpers || []).map(fromLegacyHelper));
      }
    } catch (err) {
      setError(apiErr(err, "Failed to load team members."));
    } finally {
      setLoading(false);
    }
  }, [vendorId, canManage, isPlatformAdmin, access.loading]);

  useEffect(() => { fetchStaff(); }, [fetchStaff]);

  useEffect(() => {
    if (!vendorId || !canManage) {
      setLocations([]);
      return undefined;
    }
    let live = true;
    apiVendorTeamLocations(vendorId)
      .then(({ data }) => { if (live) setLocations(Array.isArray(data?.locations) ? data.locations : []); })
      // A server without branches, or a refusal: the page works as before.
      .catch(() => { if (live) setLocations([]); });
    return () => { live = false; };
  }, [vendorId, canManage]);

  // Same vendor picker the POS fleet console uses, so "which vendor" means the
  // same thing on both screens.
  useEffect(() => {
    if (!isPlatformAdmin) return undefined;
    if (vendorTimer.current) clearTimeout(vendorTimer.current);
    const query = vendorQuery.trim();
    if (query.length < 2) {
      setVendorResults([]);
      return undefined;
    }
    vendorTimer.current = setTimeout(async () => {
      setVendorSearching(true);
      try {
        const { data } = await apiEntitySearch("VENDOR", query, 12);
        setVendorResults(data.results || []);
      } catch (_error) {
        setVendorResults([]);
      } finally {
        setVendorSearching(false);
      }
    }, 280);
    return () => vendorTimer.current && clearTimeout(vendorTimer.current);
  }, [vendorQuery, isPlatformAdmin]);

  // One runner for every write: busy flag, refetch, toast, error in the dialog.
  const run = async (fn, successMsg) => {
    setBusy(true);
    setModalError("");
    try {
      const result = await fn();
      await fetchStaff({ silent: true });
      if (successMsg) toast.success(successMsg);
      return result || true;
    } catch (err) {
      const msg = apiErr(err, "That did not work.");
      setModalError(msg);
      toast.error(msg);
      return false;
    } finally {
      setBusy(false);
    }
  };

  // ── Validation shared by add and edit ─────────────────────────────────────
  const validate = (values, setErrors) => {
    const errs = { ...EMPTY_ERRORS };
    let ok = true;
    if (!values.firstName.trim()) { errs.firstName = "First name is required"; ok = false; }
    if (!values.lastName.trim()) { errs.lastName = "Last name is required"; ok = false; }
    if (!values.email.trim() || !EMAIL_RE.test(values.email.trim())) {
      errs.email = "A valid email address is required";
      ok = false;
    }
    setErrors(errs);
    return ok;
  };

  // ── Add ───────────────────────────────────────────────────────────────────
  const openCreate = () => {
    setForm(EMPTY_FORM);
    setFormErrors(EMPTY_ERRORS);
    setModalError("");
    setBranchTried(false);
    setCreateOpen(true);
  };
  const closeCreate = () => { if (!saving) setCreateOpen(false); };

  const handleCreate = async () => {
    if (saving || !validate(form, setFormErrors)) return;
    const branchScope = normalizeBranchScope(form.branchScope);
    if (hasBranches && branchScopeError(branchScope)) {
      setBranchTried(true);
      return;
    }
    setSaving(true);
    setModalError("");
    try {
      const res = await apiVendorTeamAddMember(vendorId, {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        roleKey: form.roleKey || DEFAULT_ROLE,
        // Sent only when limited: the whole business is the server default.
        ...(hasBranches && branchScope.mode === "BRANCHES" ? { branchScope } : {}),
        // No password: the backend emails a set-password invitation.
      });
      const data = unwrapVendorTeam(res);
      const member = data?.member;
      if (data?.inviteEmailSent === false) {
        toast.success(`${form.firstName.trim()} was added to the team.`);
        toast.warn(
          `The invitation email did not go out${data?.invite?.message ? `: ${data.invite.message}` : "."} Use Resend invite on their row once that is fixed.`,
          { autoClose: 12000 }
        );
      } else {
        toast.success(`${form.firstName.trim()} was added. We emailed ${form.email.trim()} a link to set their password.`);
        if (member?.userId && data?.invite?.deliveryRefId) {
          setDeliveryRefs((refs) => ({ ...refs, [member.userId]: data.invite.deliveryRefId }));
        }
      }
      setCreateOpen(false);
      fetchStaff({ silent: true });
    } catch (err) {
      setModalError(apiErr(err, "Failed to add team member."));
    } finally {
      setSaving(false);
    }
  };

  // ── Edit ──────────────────────────────────────────────────────────────────
  const openEdit = (member) => {
    setEditTarget(member);
    setEditForm({
      firstName: member.firstName || "",
      lastName: member.lastName || "",
      email: member.email || "",
      roleKey: member.roleKey || DEFAULT_ROLE,
      branchScope: normalizeBranchScope(member.branchScope),
    });
    setEditErrors(EMPTY_ERRORS);
    setModalError("");
    setBranchTried(false);
  };
  const closeEdit = () => { if (!saving) setEditTarget(null); };

  const handleEdit = async () => {
    if (saving || !editTarget || !validate(editForm, setEditErrors)) return;
    const branchChanged = showBranches && !sameBranchScope(editForm.branchScope, editTarget.branchScope);
    if (branchChanged && branchScopeError(editForm.branchScope)) {
      setBranchTried(true);
      return;
    }
    setSaving(true);
    setModalError("");
    try {
      const details = { firstName: editForm.firstName.trim(), lastName: editForm.lastName.trim(), email: editForm.email.trim() };
      const detailsChanged =
        details.firstName !== (editTarget.firstName || "") ||
        details.lastName !== (editTarget.lastName || "") ||
        details.email !== (editTarget.email || "");
      // A legacy member has no row yet: choosing any role, the default
      // included, writes one and makes their access deliberate.
      const roleChanged = editForm.roleKey !== editTarget.roleKey || editTarget.source === "LEGACY_MEMBER";
      if (detailsChanged) await apiVendorTeamUpdateMemberDetails(editTarget.userId, details);
      if (roleChanged) await apiVendorTeamChangeRole(vendorId, editTarget.userId, editForm.roleKey);
      if (branchChanged) {
        await apiVendorTeamSetBranches(vendorId, editTarget.userId, normalizeBranchScope(editForm.branchScope));
      }
      toast.success(`${details.firstName} was updated.`);
      setEditTarget(null);
      fetchStaff({ silent: true });
    } catch (err) {
      setModalError(branchAwareMessage(err, "Failed to update team member."));
    } finally {
      setSaving(false);
    }
  };

  // ── Invite ────────────────────────────────────────────────────────────────
  const handleResend = async (member) => {
    if (resendingId) return;
    setResendingId(member.userId);
    try {
      const res = await apiVendorTeamResendInvite(vendorId, member.userId);
      const data = unwrapVendorTeam(res);
      toast.success(
        member.hasSignedIn
          ? `Sent ${fullName(member)} a new link to set their password.`
          : `Sent a new invitation to ${data?.lastInvite?.sentTo || member.email}. Earlier links no longer work.`
      );
      if (data?.deliveryRefId) setDeliveryRefs((refs) => ({ ...refs, [member.userId]: data.deliveryRefId }));
      fetchStaff({ silent: true });
    } catch (err) {
      const status = err?.response?.status;
      const msg = apiErr(err, "The invitation could not be sent.");
      if (status === 429) toast.info(msg);
      else toast.error(msg, { autoClose: 12000 });
      if (status === 502) fetchStaff({ silent: true });
    } finally {
      setResendingId(null);
    }
  };

  // ── Branch access ─────────────────────────────────────────────────────────
  const openBranches = (member) => {
    setModalError("");
    setBranchTried(false);
    setBranchDraft(normalizeBranchScope(member.branchScope));
    setBranchesFor(member);
  };
  const closeBranches = () => { if (!saving) setBranchesFor(null); };

  const handleSaveBranches = async () => {
    if (saving || !branchesFor) return;
    const scope = normalizeBranchScope(branchDraft);
    if (branchScopeError(scope)) {
      setBranchTried(true);
      return;
    }
    setSaving(true);
    setModalError("");
    try {
      await apiVendorTeamSetBranches(vendorId, branchesFor.userId, scope);
      toast.success(
        scope.mode === "ALL"
          ? `${fullName(branchesFor)} now works across the whole business.`
          : `${fullName(branchesFor)} now works at ${joinBranchNames(branchScopeNames(scope, locations).names, { max: 5 })} only.`
      );
      setBranchesFor(null);
      fetchStaff({ silent: true });
    } catch (err) {
      setModalError(branchAwareMessage(err, "Could not save branch access."));
    } finally {
      setSaving(false);
    }
  };

  // ── Role, exceptions, status, remove ──────────────────────────────────────
  const handleRoleChange = (member, roleKey) =>
    run(() => apiVendorTeamChangeRole(vendorId, member.userId, roleKey), `${fullName(member)} is now ${roleLabel(roleKey)}.`);

  const handleSaveOverrides = async (overrides) => {
    const ok = await run(() => apiVendorTeamSetOverrides(vendorId, overridesFor.userId, overrides), "Exceptions saved.");
    if (ok) setOverridesFor(null);
  };

  const confirmSuspend = (member) =>
    setConfirmState({
      title: `Suspend ${fullName(member)}?`,
      consequence:
        "They keep their login but lose every permission on this shop immediately, and their POS PIN stops working. You can reactivate them at any time.",
      confirmLabel: "Suspend",
      tone: "warning",
      action: () => apiVendorTeamSetStatus(vendorId, member.userId, "SUSPENDED"),
      successMsg: `${fullName(member)} is suspended.`,
    });

  const confirmRemove = (member) =>
    setConfirmState({
      title: `Remove ${fullName(member)} from the team?`,
      consequence:
        "They immediately lose access to your shop dashboard and the POS terminal. Re-adding them later restores access.",
      confirmLabel: "Remove",
      tone: "danger",
      action: () => apiVendorTeamRemoveMember(vendorId, member.userId),
      successMsg: `${fullName(member)} was removed from the team.`,
    });

  const runConfirm = async () => {
    const state = confirmState;
    const ok = await run(state.action, state.successMsg);
    if (ok) {
      setConfirmState(null);
      if (state.onDone) state.onDone();
    }
  };

  // ── Custom roles ──────────────────────────────────────────────────────────
  const openRole = (role) => { setModalError(""); setRoleEditor({ role }); };

  const handleSaveRole = async ({ label, description, permissions }) => {
    const existing = roleEditor?.role;
    const body = { label, description, permissions };
    const ok = await run(
      () => (existing ? apiVendorTeamUpdateRole(vendorId, existing.key, body) : apiVendorTeamCreateRole(vendorId, body)),
      existing ? `${label} saved.` : `${label} created. You can now give it to anyone on the team.`
    );
    if (ok) setRoleEditor(null);
  };

  const confirmDeleteRole = (role) =>
    setConfirmState({
      title: `Delete the ${role.label} role?`,
      consequence: role.memberCount
        ? `${role.memberCount} ${role.memberCount === 1 ? "person still has" : "people still have"} this role. Give them another role first, then delete it.`
        : "Nobody has this role. Deleting it removes it from the role list.",
      confirmLabel: "Delete role",
      tone: "danger",
      action: () => apiVendorTeamDeleteRole(vendorId, role.key),
      successMsg: `${role.label} deleted.`,
      onDone: () => setRoleEditor(null),
    });

  const legacyCount = staff.filter((m) => m.source === "LEGACY_MEMBER").length;
  const headCount = staff.length + (owner ? 1 : 0);
  const showTable = !loading && !error && (staff.length > 0 || owner);
  const columns = canManage
    ? ["Member", "Role", ...(showBranches ? ["Branches"] : []), "Exceptions", "Status", "Actions"]
    : ["Member", "Role", "Status"];
  const roles = (catalogue.roles || []).filter((r) => r.key !== "OWNER");

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={S.page}>
      <div style={S.card}>
        <div style={S.cardHeader}>
          <div>
            <h4 style={S.cardTitle}>
              My Team
              <SettingHelp topic="vendorTeamRoles" placement="right" />
            </h4>
            <p style={S.cardSubtitle}>Who helps run this shop, and what each person may do.</p>
          </div>
          {canManage && (
            <AppButton icon="fa fa-user-plus" onClick={openCreate}>Add team member</AppButton>
          )}
        </div>

        {isPlatformAdmin && (
          <div style={{ padding: "16px 24px", borderBottom: "1px solid #eef2f6", background: "#f8fafc" }}>
            {adminVendor ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <div>
                  <strong style={{ fontSize: 14, color: "#0f172a" }}>{adminVendor.label}</strong>
                  <div style={S.muted}>{adminVendor.subLabel || "Managing this vendor's team"}</div>
                </div>
                <AppButton
                  variant="quiet"
                  onClick={() => { setAdminVendor(null); setVendorQuery(""); setVendorResults([]); }}
                >
                  Change vendor
                </AppButton>
              </div>
            ) : (
              <div className="lk-field" style={{ marginBottom: 0 }}>
                <label className="lk-field__label" htmlFor="team-vendor-search">Whose team are you managing?</label>
                <input
                  id="team-vendor-search"
                  className="lk-input"
                  value={vendorQuery}
                  onChange={(event) => setVendorQuery(event.target.value)}
                  placeholder="Search vendor name or phone"
                />
                {vendorSearching && <span className="lk-field__hint">Searching...</span>}
                {vendorResults.map((vendor) => (
                  <button
                    type="button"
                    key={vendor.id}
                    onClick={() => { setAdminVendor(vendor); setVendorResults([]); setVendorQuery(""); }}
                    style={{ display: "block", width: "100%", textAlign: "left", marginTop: 8, padding: "10px 12px", border: "1px solid #e2e8f0", borderRadius: 8, background: "#fff", cursor: "pointer" }}
                  >
                    <strong style={{ fontSize: 13, color: "#0f172a" }}>{vendor.label}</strong>
                    <div style={S.muted}>{vendor.subLabel}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div style={S.cardBody}>
          {isPlatformAdmin && !adminVendor && !loading && (
            <div style={S.emptyState}>Pick a vendor above to see and manage their team.</div>
          )}

          {readOnlyMember && !loading && (
            <div className="lk-note lk-note--info" style={{ marginBottom: 14 }}>
              <i className="fa fa-info-circle" aria-hidden="true" style={{ marginTop: 2 }} />
              <span>You can see who is on the team and their roles. Only the owner, or someone allowed to manage the team, can make changes.</span>
            </div>
          )}

          {!loading && canManage && legacyCount > 0 && (
            <div className="lk-note lk-note--warning" style={{ marginBottom: 14 }}>
              <i className="fa fa-exclamation-triangle" aria-hidden="true" style={{ marginTop: 2 }} />
              <span>
                {legacyCount === 1 ? "1 team member was" : `${legacyCount} team members were`} added before roles existed
                and {legacyCount === 1 ? "is" : "are"} treated as {LEGACY_ROLE_LABEL}. Pick a role for
                {legacyCount === 1 ? " them" : " each"} so access is a deliberate choice.
              </span>
            </div>
          )}

          {loading && <LoadingCenter text="Loading team members..." />}

          {!loading && error && (
            <Alert
              type="error"
              msg={
                <span>
                  {error}{" "}
                  <AppButton size="sm" variant="quiet" onClick={() => fetchStaff()} style={{ marginLeft: 8 }}>Retry</AppButton>
                </span>
              }
            />
          )}

          {!loading && !error && vendorId && staff.length === 0 && !owner && (
            <div style={S.emptyState}>
              <p style={{ fontWeight: 700, fontSize: 14, color: "#1e293b", margin: "0 0 6px" }}>No team members yet</p>
              <p style={{ fontSize: 13, maxWidth: 380, margin: "0 auto 16px", lineHeight: 1.6 }}>
                {canManage
                  ? "Add the people who help with orders, the catalogue and customer questions. Each gets their own login and a role, so every action is traceable."
                  : "Your shop owner has not added any team members yet."}
              </p>
              {canManage && <AppButton icon="fa fa-user-plus" onClick={openCreate}>Add your first team member</AppButton>}
            </div>
          )}

          {showTable && (
            <>
              <p style={S.count}>
                {headCount} {headCount === 1 ? "person" : "people"}
                {owner ? ` · 1 owner, ${staff.length} team member${staff.length === 1 ? "" : "s"}` : ""}
              </p>
              <div style={{ overflowX: "auto" }}>
                <table style={{ ...S.table, minWidth: canManage ? 760 : 480 }}>
                  <thead>
                    <tr>
                      {columns.map((h) => (
                        <th key={h} style={h === "Actions" ? { ...S.th, textAlign: "right" } : S.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {owner && (
                      <tr>
                        <td style={S.td}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={S.avatar}>{initials(owner)}</div>
                            <span style={{ minWidth: 0 }}>
                              <span style={{ fontWeight: 600, display: "block" }}>{fullName(owner)}</span>
                              <span style={S.muted}>{owner.email}</span>
                            </span>
                          </div>
                        </td>
                        <td style={S.td}><RoleChip roleKey="OWNER" label="Owner" /></td>
                        {canManage && showBranches && (
                          <td style={S.td}><BranchCell branchScope={WHOLE_BUSINESS} locations={locations} /></td>
                        )}
                        {canManage && <td style={{ ...S.td, ...S.muted }}>None</td>}
                        <td style={S.td}><StatusPill status="ACTIVE" /></td>
                        {canManage && <td style={{ ...S.td, ...S.muted, textAlign: "right" }}>Owner, not editable</td>}
                      </tr>
                    )}
                    {staff.map((m) => {
                      const isLegacy = m.source === "LEGACY_MEMBER";
                      const isSelf = String(m.userId) === String(me._id);
                      const editable = canManage && !isSelf;
                      const role = roleByKey.get(m.roleKey);
                      const exceptions = m.customPermissions?.length || 0;
                      const suspended = m.status === "SUSPENDED";
                      const showResend = editable && !suspended && m.email && (!m.hasSignedIn || (m.lastInvite && !m.lastInvite.sent));
                      return (
                        <tr key={m.userId}>
                          <td style={S.td}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <div style={{ ...S.avatar, opacity: suspended ? 0.5 : 1 }}>{initials(m)}</div>
                              <span style={{ minWidth: 0 }}>
                                <span style={{ fontWeight: 600, display: "block" }}>
                                  {fullName(m)}
                                  {isSelf && <span style={{ ...S.muted, fontWeight: 400 }}> (you)</span>}
                                </span>
                                <span style={S.muted}>{m.email || "No email"}</span>
                                {canManage && <InviteStatus member={m} deliveryRef={deliveryRefs[m.userId]} />}
                              </span>
                            </div>
                          </td>
                          <td style={S.td}>
                            {editable ? (
                              <>
                                <TeamRoleSelect
                                  roles={catalogue.roles}
                                  value={m.roleKey || DEFAULT_ROLE}
                                  onChange={(roleKey) => handleRoleChange(m, roleKey)}
                                  legacyKey={isLegacy ? DEFAULT_ROLE : null}
                                  disabled={busy}
                                  size="sm"
                                  aria-label={`Role for ${fullName(m)}`}
                                />
                                {isLegacy && <div style={{ ...S.muted, marginTop: 3 }}>No role chosen yet</div>}
                              </>
                            ) : (
                              <RoleChip
                                roleKey={m.roleKey || DEFAULT_ROLE}
                                builtin={role ? role.builtin !== false : true}
                                label={isLegacy ? LEGACY_ROLE_LABEL : role?.label || m.roleKey}
                              />
                            )}
                          </td>
                          {canManage && showBranches && (
                            <td style={S.td}><BranchCell branchScope={m.branchScope} locations={locations} /></td>
                          )}
                          {canManage && (
                            <td style={S.td}>
                              <AppButton
                                size="sm"
                                variant={exceptions ? "outline" : "quiet"}
                                disabled={!editable || busy}
                                title={editable ? "Allow or block single permissions on top of the role" : undefined}
                                onClick={() => { setModalError(""); setOverridesFor(m); }}
                              >
                                {exceptions ? `${exceptions} exception${exceptions === 1 ? "" : "s"}` : "None"}
                              </AppButton>
                            </td>
                          )}
                          <td style={S.td}>
                            <StatusPill status={m.status || (m.isActive === false ? "SUSPENDED" : "ACTIVE")} />
                            {m.isActive === false && <div style={{ ...S.muted, marginTop: 3 }}>Login disabled</div>}
                          </td>
                          {canManage && (
                            <td style={{ ...S.td, textAlign: "right" }}>
                              {editable ? (
                                <div style={{ display: "inline-flex", gap: 6, justifyContent: "flex-end", whiteSpace: "nowrap" }}>
                                  {supportSession && !suspended && (
                                    <AppButton
                                      size="sm"
                                      variant="outline"
                                      icon="fa fa-key"
                                      title="Lusuku Support: set their sign-in email and password, then send it to them yourself"
                                      onClick={() => setSetLoginFor(m)}
                                    >
                                      Set password
                                    </AppButton>
                                  )}
                                  {showResend && (
                                    <AppButton
                                      size="sm"
                                      variant="outline"
                                      icon="fa fa-envelope"
                                      loading={resendingId === m.userId}
                                      disabled={Boolean(resendingId) && resendingId !== m.userId}
                                      title="Email them a fresh link. Earlier links stop working."
                                      onClick={() => handleResend(m)}
                                    >
                                      Resend invite
                                    </AppButton>
                                  )}
                                  {showBranches && (
                                    <AppButton
                                      size="sm"
                                      variant="quiet"
                                      icon="fa fa-store"
                                      disabled={busy}
                                      title="Which branches they work at"
                                      onClick={() => openBranches(m)}
                                    >
                                      Branches
                                    </AppButton>
                                  )}
                                  <AppButton
                                    size="sm"
                                    variant="quiet"
                                    icon="fa fa-pencil-alt"
                                    disabled={busy}
                                    aria-label={`Edit ${fullName(m)}`}
                                    title="Edit name, email and role"
                                    onClick={() => openEdit(m)}
                                  />
                                  {suspended ? (
                                    <AppButton
                                      size="sm"
                                      variant="quiet"
                                      icon="fa fa-play"
                                      disabled={busy}
                                      onClick={() => run(() => apiVendorTeamSetStatus(vendorId, m.userId, "ACTIVE"), `${fullName(m)} is active again.`)}
                                    >
                                      Reactivate
                                    </AppButton>
                                  ) : (
                                    <AppButton
                                      size="sm"
                                      variant="quiet"
                                      icon="fa fa-pause"
                                      disabled={busy}
                                      aria-label={`Suspend ${fullName(m)}`}
                                      title="Suspend: pause their access without removing them"
                                      onClick={() => confirmSuspend(m)}
                                    />
                                  )}
                                  <AppButton
                                    size="sm"
                                    variant="danger"
                                    icon="fa fa-trash"
                                    disabled={busy}
                                    aria-label={`Remove ${fullName(m)}`}
                                    title="Remove from the team"
                                    onClick={() => confirmRemove(m)}
                                  />
                                </div>
                              ) : (
                                <span style={S.muted}>Your own account</span>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Roles: presets and this shop's own ───────────────────────────── */}
      {vendorId && !loading && roles.length > 0 && (
        <div style={S.card}>
          <div style={S.cardHeader}>
            <div>
              <h4 style={S.cardTitle}>Roles</h4>
              <p style={S.cardSubtitle}>
                A role is a set of permissions. Use a built-in role, or create your own when none fits.
              </p>
            </div>
            {canManage && (
              <AppButton variant="outline" icon="fa fa-plus" onClick={() => openRole(null)}>New role</AppButton>
            )}
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ ...S.table, minWidth: 640 }}>
              <thead>
                <tr>
                  <th style={{ ...S.th, paddingLeft: 24 }}>Role</th>
                  <th style={S.th}>What it allows</th>
                  <th style={S.th}>People</th>
                  <th style={{ ...S.th, textAlign: "right", paddingRight: 24 }} />
                </tr>
              </thead>
              <tbody>
                {roles.map((r) => (
                  <tr key={r.key}>
                    <td style={{ ...S.td, paddingLeft: 24, whiteSpace: "nowrap" }}>
                      <RoleChip roleKey={r.key} builtin={r.builtin !== false} label={r.label} />
                      {r.builtin === false && <div style={{ ...S.muted, marginTop: 3 }}>Your role</div>}
                    </td>
                    <td style={{ ...S.td, color: "#475569", maxWidth: 420 }}>
                      {r.description || `${r.permissions.length} permissions`}
                      <div style={{ ...S.muted, marginTop: 2 }}>{r.permissions.length} of {catalogue.permissions.length} permissions</div>
                    </td>
                    <td style={S.td}>{r.memberCount || 0}</td>
                    <td style={{ ...S.td, textAlign: "right", paddingRight: 24 }}>
                      <AppButton size="sm" variant="quiet" onClick={() => openRole(r)}>
                        {r.builtin !== false || !canManage ? "View" : "Edit"}
                      </AppButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ ...S.muted, padding: "12px 24px 16px", margin: 0 }}>
            Exceptions on a person can allow a permission their role lacks or block one it has. A block always wins.
            <SettingHelp topic="vendorTeamExceptions" />
          </p>
        </div>
      )}

      {/* ── POS terminal PINs (team:manage) ───────────────────────────────── */}
      {canManage && (
        <div style={{ margin: "24px 28px" }}>
          {isPlatformAdmin
            ? <PosPinManager title="POS Terminal PINs" admin vendorUserId={vendorId} />
            : <PosPinManager title="POS Terminal PINs" />}
        </div>
      )}

      {/* ── Add ───────────────────────────────────────────────────────────── */}
      <AppDialog
        open={createOpen}
        title="Add team member"
        subtitle="They get their own login. You choose what they may do."
        onClose={closeCreate}
        busy={saving}
        footer={
          <>
            <AppButton variant="quiet" onClick={closeCreate} disabled={saving}>Cancel</AppButton>
            <AppButton icon="fa fa-user-plus" loading={saving} onClick={handleCreate}>Add team member</AppButton>
          </>
        }
      >
        <Alert type="error" msg={modalError} />
        <MemberFields values={form} errors={formErrors} onChange={setForm} onEnter={handleCreate} />
        <div className="lk-field">
          <label className="lk-field__label" htmlFor="member-role-add">Role</label>
          <TeamRoleSelect
            id="member-role-add"
            roles={catalogue.roles}
            value={form.roleKey}
            onChange={(roleKey) => setForm({ ...form, roleKey })}
            block
          />
          <span className="lk-field__hint">
            {roleByKey.get(form.roleKey)?.description || "What this person may do. You can add exceptions later."}
          </span>
        </div>
        {hasBranches && (
          <BranchAccessField
            id="member-branches-add"
            value={form.branchScope}
            onChange={(branchScope) => setForm({ ...form, branchScope })}
            locations={locations}
            disabled={saving}
            showError={branchTried}
          />
        )}
        <div className="lk-note lk-note--info">
          <i className="fa fa-envelope" aria-hidden="true" style={{ marginTop: 2 }} />
          <span>
            No password needed. We email <strong>{form.email.trim() || "them"}</strong> a link to set their own
            password. It works for 48 hours, and you can send a new one from this page if it does not arrive.
          </span>
        </div>
      </AppDialog>

      {/* ── Edit ──────────────────────────────────────────────────────────── */}
      <AppDialog
        open={Boolean(editTarget)}
        title={editTarget ? `Edit ${fullName(editTarget)}` : "Edit team member"}
        subtitle={editTarget?.email}
        onClose={closeEdit}
        busy={saving}
        footer={
          <>
            <AppButton variant="quiet" onClick={closeEdit} disabled={saving}>Cancel</AppButton>
            <AppButton loading={saving} onClick={handleEdit}>Save changes</AppButton>
          </>
        }
      >
        <Alert type="error" msg={modalError} />
        <MemberFields values={editForm} errors={editErrors} onChange={setEditForm} onEnter={handleEdit} />
        <div className="lk-field">
          <label className="lk-field__label" htmlFor="member-role-edit">Role</label>
          <TeamRoleSelect
            id="member-role-edit"
            roles={catalogue.roles}
            value={editForm.roleKey}
            onChange={(roleKey) => setEditForm({ ...editForm, roleKey })}
            legacyKey={editTarget?.source === "LEGACY_MEMBER" ? DEFAULT_ROLE : null}
            block
          />
          <span className="lk-field__hint">
            {editTarget?.source === "LEGACY_MEMBER"
              ? `No role chosen yet, so they are treated as ${LEGACY_ROLE_LABEL}. Saving records the role you pick.`
              : roleByKey.get(editForm.roleKey)?.description || ""}
          </span>
        </div>
        {showBranches && editTarget && (
          <BranchAccessField
            id="member-branches-edit-details"
            value={editForm.branchScope}
            onChange={(branchScope) => setEditForm({ ...editForm, branchScope })}
            locations={locations}
            disabled={saving}
            showError={branchTried}
          />
        )}
        {editTarget && (
          <div className="lk-note" style={{ alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
            <span style={{ display: "flex", gap: 10, alignItems: "flex-start", flex: "1 1 260px" }}>
              <i className="fa fa-lock" aria-hidden="true" style={{ marginTop: 2 }} />
              <span>
                {editTarget.hasSignedIn
                  ? "Locked out? Send them a link to set a new password. Their old password keeps working until they use it."
                  : "They have not signed in yet. If the invitation did not arrive, send a new one. Earlier links stop working."}
              </span>
            </span>
            <AppButton
              size="sm"
              variant="outline"
              icon="fa fa-envelope"
              loading={resendingId === editTarget.userId}
              disabled={!editTarget.email || editTarget.status === "SUSPENDED"}
              onClick={() => handleResend(editTarget)}
            >
              {editTarget.hasSignedIn ? "Send password link" : "Resend invite"}
            </AppButton>
          </div>
        )}
      </AppDialog>

      {/* ── Branch access ─────────────────────────────────────────────────── */}
      <AppDialog
        open={Boolean(branchesFor)}
        title={branchesFor ? `Branches for ${fullName(branchesFor)}` : "Branches"}
        subtitle={branchesFor?.email}
        onClose={closeBranches}
        busy={saving}
        footer={
          <>
            <AppButton variant="quiet" onClick={closeBranches} disabled={saving}>Cancel</AppButton>
            <AppButton loading={saving} onClick={handleSaveBranches}>Save branch access</AppButton>
          </>
        }
      >
        <Alert type="error" msg={modalError} />
        <BranchAccessField
          id="member-branches-edit"
          value={branchDraft}
          onChange={setBranchDraft}
          locations={locations}
          disabled={saving}
          showError={branchTried}
        />
      </AppDialog>

      {/* ── Exceptions (shared with the organizer team page) ──────────────── */}
      {overridesFor && (
        <OverridesModal
          member={{ ...overridesFor, name: fullName(overridesFor), roleKey: overridesFor.roleKey || DEFAULT_ROLE }}
          roleLabel={roleLabel(overridesFor.roleKey || DEFAULT_ROLE)}
          permissions={catalogue.permissions || []}
          rolePermissions={roleByKey.get(overridesFor.roleKey || DEFAULT_ROLE)?.permissions || []}
          grantableKeys={grantableKeys}
          busy={busy}
          error={modalError}
          onClose={() => setOverridesFor(null)}
          onSubmit={handleSaveOverrides}
        />
      )}

      {/* ── Role editor (shared with the organizer team page) ─────────────── */}
      {roleEditor && (
        <RoleEditorDialog
          role={roleEditor.role}
          permissions={catalogue.permissions || []}
          grantableKeys={grantableKeys}
          lockedKeys={catalogue.baselinePermissions || []}
          canEdit={canManage}
          busy={busy}
          error={modalError}
          onClose={() => setRoleEditor(null)}
          onSubmit={handleSaveRole}
          onDelete={roleEditor.role && roleEditor.role.builtin === false ? () => confirmDeleteRole(roleEditor.role) : null}
        />
      )}

      <AppConfirmDialog
        open={Boolean(confirmState)}
        title={confirmState?.title}
        consequence={confirmState?.consequence}
        confirmLabel={confirmState?.confirmLabel}
        tone={confirmState?.tone}
        busy={busy}
        onConfirm={runConfirm}
        onClose={() => setConfirmState(null)}
      />

      {supportSession && (
        <SetVendorLoginDialog
          open={Boolean(setLoginFor)}
          onClose={() => setSetLoginFor(null)}
          vendorType={supportSession.vertical}
          vendorId={supportSession.vendorId}
          vendorName={supportSession.vendorName}
          memberUserId={setLoginFor?.userId || null}
          adminSession
          onSaved={() => fetchStaff({ silent: true })}
        />
      )}
    </div>
  );
};

export default VendorStaffView;
